import { TradingViewResolution, useDoubleContractKlineStore, useProjectTheme, useResponsive, useContractChangeSymbol, doubleMSocketService } from 'store'
import type { IChartingLibraryWidget, ResolutionString, WidgetOverrides } from 'charting-library'
import { PLATFORM_CONFIG, ProjectTheme, TRADING_VIEW_OVERRIDES } from 'platform-config'
import { useTradingViewDatafeed } from './use-contract-trading-view-datafeed'
import dayjs from 'dayjs'
/**
 * 用于SpotKline组件
 */
export const useContractKlineComponent = () => {
  const activeKey = ref<'LINE' | 'MAP'>('LINE')
  const tradingViewMounted = ref<HTMLDivElement>()
  const { createDatafeed, getLocale } = useTradingViewDatafeed()
  const klineStore = useDoubleContractKlineStore()
  const widget = ref<IChartingLibraryWidget>()
  const themeStore = useProjectTheme()
  const responsive = useResponsive()
  const loadingChart = ref(true)

  const resolutionList = [
    {
      label: '1分钟',
      value: TradingViewResolution.OneMinute,
    },
    {
      label: '5分钟',
      value: TradingViewResolution.FiveMinutes,
    },
    {
      label: '15分钟',
      value: TradingViewResolution.FifteenMinutes,
    },
    {
      label: '30分钟',
      value: TradingViewResolution.ThirtyMinutes,
    },
    {
      label: '1小时',
      value: TradingViewResolution.OneHour,
    },
    {
      label: '4小时',
      value: TradingViewResolution.FourHour,
    },
    {
      label: '1天',
      value: TradingViewResolution.OneDay,
    },
    {
      label: '1周',
      value: TradingViewResolution.OneWeek,
    },
    {
      label: '1月',
      value: TradingViewResolution.OneMonth,
    },
  ]

  const maList = [
    {
      num: 7,
      color: '#642d92',
    },
    {
      num: 25,
      color: '#5278a3',
    },
    {
      num: 99,
      color: '#238031',
    },
  ]

  onMounted(() => {
    klineStore.listenKline()
  })

  const setResolution = (resolution: ResolutionString) => {
    klineStore.setResolution(resolution)
    widget.value?.chart().setResolution(resolution)
  }

  const getOverrides = () => {
    const overrides: Partial<WidgetOverrides> = {
      ...TRADING_VIEW_OVERRIDES,
      'scalesProperties.fontSize': responsive.isMobile ? 12 : 13,
      'paneProperties.backgroundGradientStartColor': themeStore.theme.colors.bg.base,
      'paneProperties.backgroundGradientEndColor': themeStore.theme.colors.bg.base,
      'scalesProperties.showSymbolLabels': false,
      'scalesProperties.showStudyLastValue': false, // 隐藏右侧Y轴上的指标值
      'scalesProperties.showStudyPlotLabels': false, // 隐藏指标标签
      volumePaneSize: 'medium',
    }

    return overrides
  }

  useContractChangeSymbol((currentSymbolId) => {
    widget.value?.chart().setSymbol(`${currentSymbolId}`)
  })

  const formatDate = (date: Date) => {
    return dayjs(date).format('HH:mm')
  }

  // 切换active 值
  const onStepChange = (key: 'LINE' | 'MAP') => {
    activeKey.value = key
  }
  const initWidget = async () => {
    if (!tradingViewMounted.value) return
    const isDark = themeStore.themeType === ProjectTheme.Dark
    const overrides: Partial<WidgetOverrides> = getOverrides()

    widget.value = new window.TradingView.widget({
      symbol: `${klineStore.currentSymbolId}`,
      interval: klineStore.currentResolution,
      locale: getLocale(),

      container: tradingViewMounted.value,
      theme: isDark ? 'dark' : 'light',
      library_path: '/charting-library/',
      custom_css_url: '/charting-library/custom-trading-view.css',
      custom_font_family: PLATFORM_CONFIG.font.family,
      datafeed: await createDatafeed({
        symbol: klineStore.currentSymbolId!,
      }),
      overrides,

      loading_screen: {
        backgroundColor: themeStore.theme.colors.bg.base,
        foregroundColor: themeStore.theme.colors.primary,
      },

      autosize: true,

      disabled_features: [
        'use_localstorage_for_settings', // 禁用本地存储
        'header_screenshot', // 禁用截图
        'header_symbol_search', // 禁用符号搜索
        'header_undo_redo', // 禁用撤销重做
        'header_compare', // 禁用比较
        'timeframes_toolbar', // 禁用时间框架工具栏
        'header_chart_type', // 禁用图表类型
        'header_resolutions', // 头部分辨率
        'header_widget', // 头部分辨率
        'volume_force_overlay', // kline与成交量是否重合
        'property_pages', // 移动端禁用属性
        'show_chart_property_page', // 移动端禁用属性
        'iframe_loading_compatibility_mode', // 移动端禁用属性
      ],

      studies_overrides: {
        'volume.volume.color.0': PLATFORM_CONFIG.colors.down, //  下跌成交量颜色
        'volume.volume.color.1': PLATFORM_CONFIG.colors.up, // 上涨成交量颜色
      },

      custom_formatters: {
        timeFormatter: {
          format: formatDate,
          formatLocal: formatDate,
        },

        dateFormatter: {
          format: formatDate,
          formatLocal: formatDate,
        },

        priceFormatterFactory: () => {
          return {
            format: (price: number) => {
              return price.cutDecimal(klineStore.coinToPrecision).formatCommonMoney().decimalPadZero(klineStore.coinToPrecision)
            },
          }
        },

        studyFormatterFactory() {
          return null
        },
      },
    })

    widget.value.onChartReady(async () => {
      for (const item of maList) {
        widget.value?.chart().createStudy(
          `Moving Average`,
          true,
          true,
          {
            length: item.num,
          },
          {
            'plot.color': item.color,
            'plot.linewidth': 1,
          },
        )
      }

      widget.value?.onChartReady(() => {
        // 减少右侧留白 - 通过价格轴设置
        const mainPane = widget.value?.chart().getPanes()[0]
        if (mainPane) {
          const rightPriceScales = mainPane.getRightPriceScales()
          if (rightPriceScales.length > 0) {
            // 设置价格轴为自动缩放模式，减少留白
            rightPriceScales[0].setAutoScale(true)
          }
        }
      })

      setTimeout(() => {
        loadingChart.value = false
      }, 100)
    })
  }

  watch(
    () => themeStore.themeType,
    (themeType) => {
      if (themeType === ProjectTheme.Dark) {
        widget.value?.changeTheme('dark').then(() => {
          widget.value?.applyOverrides(getOverrides())
        })
      } else {
        widget.value?.changeTheme('light').then(() => {
          widget.value?.applyOverrides(getOverrides())
        })
      }
    },
  )

  onMounted(() => {
    nextTick(async () => {
      await initWidget()
    })
    doubleMSocketService.onReconnect(() => {
      klineStore.subscribeKline()
    })
  })

  return {
    maList,
    activeKey,
    onStepChange,
    loadingChart,
    setResolution,
    resolutionList,
    tradingViewMounted,
  }
}
